import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { SelectAllMenuItem } from '../../../../shared/components';
import type { UseMutationResult } from '@tanstack/react-query';
import type {
  ClientJobRole,
  CreateSingleUserRequest,
  CreateSingleUserResponse,
  ServiceOverrides,
} from '../api/clientManagement';

type ServiceKey = keyof ServiceOverrides;

const SERVICE_LABELS: Record<ServiceKey, string> = {
  learning_locker: 'Learning Locker',
  occupational_health: 'Occupational Health',
  wearables: 'Wearables',
  akea_life: 'Akea Life',
};

interface CreateUserDialogProps {
  open: boolean;
  clientId: string;
  departments: { id: string; name: string }[];
  jobRoles: ClientJobRole[];
  regions: { id: string; name: string }[];
  depots: { id: string; name: string }[];
  createUserMutation: UseMutationResult<
    CreateSingleUserResponse,
    unknown,
    { clientId: string; payload: CreateSingleUserRequest }
  >;
  onClose: () => void;
}

interface FormState {
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  requires_consent: boolean;
  is_admin: boolean;
  delivery: 'email' | 'csv';
  department_ids: string[];
  job_role_ids: string[];
  region_ids: string[];
  depot_ids: string[];
  service_overrides: Record<ServiceKey, boolean | null>;
}

const initialForm: FormState = {
  email: '',
  first_name: '',
  last_name: '',
  company: '',
  requires_consent: true,
  is_admin: false,
  delivery: 'email',
  department_ids: [],
  job_role_ids: [],
  region_ids: [],
  depot_ids: [],
  service_overrides: {
    learning_locker: null,
    occupational_health: null,
    wearables: null,
    akea_life: null,
  },
};

export const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  clientId,
  departments,
  jobRoles,
  regions,
  depots,
  createUserMutation,
  onClose,
}) => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [result, setResult] = useState<CreateSingleUserResponse['data'] | null>(null);

  const handleClose = () => {
    setForm(initialForm);
    setResult(null);
    createUserMutation.reset();
    onClose();
  };

  const handleSubmit = () => {
    const payload: CreateSingleUserRequest = {
      email: form.email.trim(),
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      company: form.company.trim() || undefined,
      requires_consent: form.requires_consent,
      is_admin: form.is_admin,
      delivery: form.delivery,
      department_ids: form.department_ids.length ? form.department_ids : undefined,
      job_role_ids: form.job_role_ids.length ? form.job_role_ids : undefined,
      region_ids: form.region_ids.length ? form.region_ids : undefined,
      depot_ids: form.depot_ids.length ? form.depot_ids : undefined,
    };

    const hasOverrides = Object.values(form.service_overrides).some((v) => v !== null);
    if (hasOverrides) {
      payload.service_overrides = form.service_overrides;
    }

    createUserMutation.mutate(
      { clientId, payload },
      {
        onSuccess: (response) => {
          setResult(response.data);
        },
      },
    );
  };

  const isValid = form.email.trim() && form.first_name.trim() && form.last_name.trim();

  if (result) {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
        <DialogTitle>User Created</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity='success'>
              {result.first_name} {result.last_name} has been created successfully.
            </Alert>
            <Stack spacing={1}>
              <Typography variant='body2'>
                <strong>Email:</strong> {result.email}
              </Typography>
              {result.password && (
                <Typography variant='body2'>
                  <strong>Temporary Password:</strong> {result.password}
                </Typography>
              )}
              {result.email_sent && (
                <Alert severity='info' sx={{ mt: 1 }}>
                  Login credentials have been emailed to the user.
                </Alert>
              )}
              {result.email_error && (
                <Alert severity='warning' sx={{ mt: 1 }}>
                  Email failed: {result.email_error}
                </Alert>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
      <DialogTitle>Create User</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {createUserMutation.isError && (
            <Alert severity='error'>
              {(createUserMutation.error as any)?.response?.data?.message || 'Failed to create user'}
            </Alert>
          )}

          <Typography variant='subtitle2'>Basic Details</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label='First Name'
              size='small'
              required
              fullWidth
              value={form.first_name}
              onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))}
            />
            <TextField
              label='Last Name'
              size='small'
              required
              fullWidth
              value={form.last_name}
              onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label='Email'
              size='small'
              required
              fullWidth
              type='email'
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            />
            <TextField
              label='Company'
              size='small'
              fullWidth
              value={form.company}
              onChange={(e) => setForm((prev) => ({ ...prev, company: e.target.value }))}
            />
          </Stack>

          <Typography variant='subtitle2'>Credentials Delivery</Typography>
          <FormControl size='small' fullWidth>
            <InputLabel>Delivery Method</InputLabel>
            <Select
              label='Delivery Method'
              value={form.delivery}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  delivery: e.target.value as 'email' | 'csv',
                }))
              }
            >
              <MenuItem value='email'>Send login email to user</MenuItem>
              <MenuItem value='csv'>Show password (do not email)</MenuItem>
            </Select>
          </FormControl>

          <Typography variant='subtitle2'>Assignments</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {departments.length > 0 && (
              <FormControl size='small' fullWidth>
                <InputLabel>Departments</InputLabel>
                <Select
                  label='Departments'
                  multiple
                  value={form.department_ids}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      department_ids: e.target.value as string[],
                    }))
                  }
                  renderValue={(selected) =>
                    selected.map((id) => departments.find((d) => d.id === id)?.name ?? id).join(', ')
                  }
                >
                  <SelectAllMenuItem
                    allIds={departments.map((d) => d.id)}
                    selectedIds={form.department_ids}
                    onChange={(ids) => setForm((prev) => ({ ...prev, department_ids: ids }))}
                  />
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      <Checkbox checked={form.department_ids.includes(dept.id)} size='small' />
                      <ListItemText primary={dept.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {jobRoles.length > 0 && (
              <FormControl size='small' fullWidth>
                <InputLabel>Job Roles</InputLabel>
                <Select
                  label='Job Roles'
                  multiple
                  value={form.job_role_ids}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      job_role_ids: e.target.value as string[],
                    }))
                  }
                  renderValue={(selected) =>
                    selected.map((id) => jobRoles.find((r) => r.id === id)?.name ?? id).join(', ')
                  }
                >
                  <SelectAllMenuItem
                    allIds={jobRoles.map((r) => r.id)}
                    selectedIds={form.job_role_ids}
                    onChange={(ids) => setForm((prev) => ({ ...prev, job_role_ids: ids }))}
                  />
                  {jobRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      <Checkbox checked={form.job_role_ids.includes(role.id)} size='small' />
                      <ListItemText primary={role.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
          {regions.length > 0 && (
            <FormControl size='small' fullWidth>
              <InputLabel>Regions</InputLabel>
              <Select
                label='Regions'
                multiple
                value={form.region_ids}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    region_ids: e.target.value as string[],
                  }))
                }
                renderValue={(selected) =>
                  selected.map((id) => regions.find((r) => r.id === id)?.name ?? id).join(', ')
                }
              >
                <SelectAllMenuItem
                  allIds={regions.map((r) => r.id)}
                  selectedIds={form.region_ids}
                  onChange={(ids) => setForm((prev) => ({ ...prev, region_ids: ids }))}
                />
                {regions.map((region) => (
                  <MenuItem key={region.id} value={region.id}>
                    <Checkbox checked={form.region_ids.includes(region.id)} size='small' />
                    <ListItemText primary={region.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {depots.length > 0 && (
            <FormControl size='small' fullWidth>
              <InputLabel>Depots</InputLabel>
              <Select
                label='Depots'
                multiple
                value={form.depot_ids}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    depot_ids: e.target.value as string[],
                  }))
                }
                renderValue={(selected) => selected.map((id) => depots.find((d) => d.id === id)?.name ?? id).join(', ')}
              >
                <SelectAllMenuItem
                  allIds={depots.map((d) => d.id)}
                  selectedIds={form.depot_ids}
                  onChange={(ids) => setForm((prev) => ({ ...prev, depot_ids: ids }))}
                />
                {depots.map((depot) => (
                  <MenuItem key={depot.id} value={depot.id}>
                    <Checkbox checked={form.depot_ids.includes(depot.id)} size='small' />
                    <ListItemText primary={depot.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Typography variant='subtitle2'>Service Overrides</Typography>
          <Typography variant='caption' color='text.secondary'>
            Leave as &ldquo;Use client default&rdquo; unless you need to override for this user specifically.
          </Typography>
          <Stack spacing={1}>
            {(Object.keys(SERVICE_LABELS) as ServiceKey[]).map((key) => (
              <Stack key={key} direction='row' alignItems='center' spacing={2}>
                <Typography variant='body2' sx={{ minWidth: 160 }}>
                  {SERVICE_LABELS[key]}
                </Typography>
                <FormControl size='small' sx={{ minWidth: 180 }}>
                  <Select
                    value={
                      form.service_overrides[key] === null
                        ? 'default'
                        : form.service_overrides[key]
                          ? 'enabled'
                          : 'disabled'
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        service_overrides: {
                          ...prev.service_overrides,
                          [key]: val === 'default' ? null : val === 'enabled',
                        },
                      }));
                    }}
                  >
                    <MenuItem value='default'>Use client default</MenuItem>
                    <MenuItem value='enabled'>Enabled</MenuItem>
                    <MenuItem value='disabled'>Disabled</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            ))}
          </Stack>

          <Typography variant='subtitle2'>Permissions</Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_admin}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_admin: e.target.checked,
                    }))
                  }
                />
              }
              label='Client Admin'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.requires_consent}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      requires_consent: e.target.checked,
                    }))
                  }
                />
              }
              label='Requires Consent'
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant='contained' disabled={!isValid || createUserMutation.isPending} onClick={handleSubmit}>
          {createUserMutation.isPending ? 'Creating...' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
